import {
  MONEY_ZERO,
  isZeroMoney,
  negateMoney,
  parseMoney,
  serializeMoney,
  subtractMoney,
  sumMoney,
  type MoneyInput,
} from '@erp/shared-utils';
import { MatchConfidenceStatus, SystemEntity } from '@erp/shared-types';
import type {
  Charge,
  EntityAssociationItem,
  PurchaseOrderLineItem,
} from '@erp/rpc';

/**
 * Builds the "Related lines" view of an AP invoice: every active line on the
 * documents the invoice is matched against (Famous load, sales order,
 * transfer order, PO), grouped by document, each marked with the invoice
 * line it is matched to. Mirrors the Famous Purchase Matching screen, where
 * AP checks that the selected lines across all files add up to the invoice.
 */

/** Header documents an AP invoice links to, in Famous review order. */
const RELATED_DOCUMENT_TYPES = [
  SystemEntity.SCM_SHIPMENT,
  SystemEntity.AR_SALES_ORDER,
  SystemEntity.IM_TRANSFER_ORDER,
  SystemEntity.INTERNAL_PURCHASE_ORDER,
] as const;

export type RelatedDocumentType = (typeof RELATED_DOCUMENT_TYPES)[number];

export interface RelatedDocumentRef {
  entityType: RelatedDocumentType;
  entityId: string;
}

export interface RelatedDocumentData extends RelatedDocumentRef {
  /** Active header charges (the API already hides reversed Famous rows). */
  charges: Charge[];
  /**
   * Header charges that don't count toward totals (Famous reversal and
   * superseded rows, informational PO charges). Shown for history only.
   */
  inactiveCharges?: Charge[];
  /** PO line items; empty for other document types. */
  lineItems: PurchaseOrderLineItem[];
  /** The PO's supplier, used to drop another vendor's goods lines. */
  supplierId?: string | null;
}

/** Why a charge row is excluded from totals. */
export type InactiveReason = 'reversal' | 'superseded' | 'inactive';

export interface RelatedLineRow {
  key: string;
  kind: 'charge' | 'line-item';
  entityType: SystemEntity;
  entityId: string;
  /** Display name; null when the source has none (UI shows a fallback). */
  label: string | null;
  detail: string | null;
  /** Signed amount as a canonical string; credits are negative. */
  amount: string;
  /** Line numbers of the invoice lines matched to this row, ascending. */
  matchedInvoiceLineNumbers: number[];
  /**
   * Line numbers of the invoice lines a split suggestion would match this row
   * to, ascending. Not a match until AP accepts it.
   */
  proposedInvoiceLineNumbers: number[];
  /** Set for history rows, which never count toward totals. */
  inactiveReason: InactiveReason | null;
}

export interface RelatedDocumentGroup extends RelatedDocumentRef {
  rows: RelatedLineRow[];
  subtotal: string;
}

export interface RelatedLinesSummary {
  groups: RelatedDocumentGroup[];
  relatedTotal: string;
  matchedTotal: string;
  /** Total of the rows a split suggestion would match, not yet accepted. */
  proposedTotal: string;
  proposedCount: number;
  unmatchedCount: number;
  invoiceTotal: string | null;
  /** Invoice total minus related total, or null without an invoice total. */
  difference: string | null;
}

interface InvoiceLineRef {
  id: string;
  lineNumber: number;
}

function isRelatedDocumentType(value: string): value is RelatedDocumentType {
  return (RELATED_DOCUMENT_TYPES as readonly string[]).includes(value);
}

/** Unique related header documents from the invoice's own associations. */
export function selectRelatedDocuments(
  invoiceAssociations: EntityAssociationItem[],
): RelatedDocumentRef[] {
  const seen = new Set<string>();
  const documents: RelatedDocumentRef[] = [];
  for (const { entity } of invoiceAssociations) {
    if (!isRelatedDocumentType(entity.entityType)) continue;
    const key = `${entity.entityType}:${entity.entityId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    documents.push({
      entityType: entity.entityType,
      entityId: entity.entityId,
    });
  }
  return documents.sort(
    (a, b) =>
      RELATED_DOCUMENT_TYPES.indexOf(a.entityType) -
      RELATED_DOCUMENT_TYPES.indexOf(b.entityType),
  );
}

/**
 * A link a split suggestion made for an invoice line: it belongs to a split
 * group and is still tentative. It becomes a match when AP accepts the group.
 */
export function isProposedSplitLink(item: EntityAssociationItem): boolean {
  return (
    !!item.metadata?.splitGroupId &&
    item.metadata.matchStatus === MatchConfidenceStatus.TENTATIVE
  );
}

function collectLineNumbers(
  invoiceLines: InvoiceLineRef[],
  lineAssociations: Map<string, EntityAssociationItem[]>,
  include: (item: EntityAssociationItem) => boolean,
): Map<string, number[]> {
  const byEntity = new Map<string, number[]>();
  for (const line of invoiceLines) {
    for (const item of lineAssociations.get(line.id) ?? []) {
      if (!include(item)) continue;
      const lineNumbers = byEntity.get(item.entity.entityId) ?? [];
      if (!lineNumbers.includes(line.lineNumber)) {
        lineNumbers.push(line.lineNumber);
      }
      byEntity.set(item.entity.entityId, lineNumbers);
    }
  }
  for (const lineNumbers of byEntity.values()) {
    lineNumbers.sort((a, b) => a - b);
  }
  return byEntity;
}

/** Matched entity id → invoice line numbers that point at it. */
export function buildMatchedLineNumbers(
  invoiceLines: InvoiceLineRef[],
  lineAssociations: Map<string, EntityAssociationItem[]>,
): Map<string, number[]> {
  return collectLineNumbers(
    invoiceLines,
    lineAssociations,
    (item) => !isProposedSplitLink(item),
  );
}

/** Entity id → invoice line numbers a still-tentative split suggestion links it to. */
export function buildProposedLineNumbers(
  invoiceLines: InvoiceLineRef[],
  lineAssociations: Map<string, EntityAssociationItem[]>,
): Map<string, number[]> {
  return collectLineNumbers(
    invoiceLines,
    lineAssociations,
    isProposedSplitLink,
  );
}

export function signedChargeAmount(charge: Charge): string {
  const amount = parseMoney(charge.amount);
  return serializeMoney(
    charge.direction === 'CREDIT' ? negateMoney(amount) : amount,
  );
}

function readString(
  metadata: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const value = metadata?.[key];
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}

/** Famous `GLDELETECODE` → why the charge is out of the totals. */
function inactiveReasonOf(charge: Charge): InactiveReason {
  const code = readString(charge.originMetadata, 'glDeleteCode');
  if (code === 'R') return 'reversal';
  if (code === 'Y') return 'superseded';
  return 'inactive';
}

/** Famous row order (`ARLOADDTLSEQ`) when present, else creation time. */
function compareCharges(a: Charge, b: Charge): number {
  const seqA = Number(a.originMetadata?.detailSeq);
  const seqB = Number(b.originMetadata?.detailSeq);
  if (Number.isFinite(seqA) && Number.isFinite(seqB) && seqA !== seqB) {
    return seqA - seqB;
  }
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}

function chargeRow(
  charge: Charge,
  matches: Map<string, number[]>,
  proposed: Map<string, number[]>,
  inactiveReason: InactiveReason | null,
): RelatedLineRow {
  const label =
    charge.chargeCategory?.label ||
    charge.chargeCategory?.value ||
    charge.sourceReference ||
    null;
  // Famous load charges carry the invoice text (e.g. "PO 24286"), which is
  // how AP tells an extra pick-up from the main freight.
  const invoiceDescription = readString(
    charge.originMetadata,
    'invoiceDescription',
  );
  return {
    key: `charge:${charge.id}`,
    kind: 'charge',
    entityType: SystemEntity.CHARGE,
    entityId: charge.id,
    label,
    detail: invoiceDescription !== label ? invoiceDescription : null,
    amount: signedChargeAmount(charge),
    matchedInvoiceLineNumbers: matches.get(charge.id) ?? [],
    proposedInvoiceLineNumbers: proposed.get(charge.id) ?? [],
    inactiveReason,
  };
}

function lineItemRow(
  lineItem: PurchaseOrderLineItem,
  matches: Map<string, number[]>,
  proposed: Map<string, number[]>,
): RelatedLineRow {
  const label =
    lineItem.productName || lineItem.description || lineItem.productSku || null;
  const detail = lineItem.productSku ?? null;
  return {
    key: `line-item:${lineItem.id}`,
    kind: 'line-item',
    entityType: SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
    entityId: lineItem.id,
    label,
    detail: detail !== label ? detail : null,
    amount: serializeMoney(lineItem.totalPrice ?? MONEY_ZERO),
    matchedInvoiceLineNumbers: matches.get(lineItem.id) ?? [],
    proposedInvoiceLineNumbers: proposed.get(lineItem.id) ?? [],
    inactiveReason: null,
  };
}

/**
 * A PO's goods lines belong to its supplier. When a carrier bills freight on
 * someone else's PO (Spear on a Bova Fresh PO), only the PO's charges are
 * the carrier's; the goods lines are another vendor's. Keep them when either
 * supplier is unknown rather than hide data.
 */
function isOtherSuppliersPO(
  document: RelatedDocumentData,
  invoiceSupplierId: string | null | undefined,
): boolean {
  return (
    !!document.supplierId &&
    !!invoiceSupplierId &&
    document.supplierId !== invoiceSupplierId
  );
}

function documentRows(
  document: RelatedDocumentData,
  matches: Map<string, number[]>,
  proposed: Map<string, number[]>,
  invoiceSupplierId: string | null | undefined,
): RelatedLineRow[] {
  const lineItems = isOtherSuppliersPO(document, invoiceSupplierId)
    ? []
    : document.lineItems;
  const activeIds = new Set(document.charges.map((charge) => charge.id));
  const charges = [
    ...document.charges,
    ...(document.inactiveCharges ?? []).filter(
      (charge) => !activeIds.has(charge.id),
    ),
  ].sort(compareCharges);
  return [
    ...lineItems.map((lineItem) => lineItemRow(lineItem, matches, proposed)),
    ...charges.map((charge) =>
      chargeRow(
        charge,
        matches,
        proposed,
        activeIds.has(charge.id) ? null : inactiveReasonOf(charge),
      ),
    ),
  ];
}

function sumRows(rows: RelatedLineRow[]): string {
  return serializeMoney(sumMoney(rows.map((row) => row.amount)));
}

export function buildRelatedLinesSummary({
  documents,
  matches,
  proposed = new Map(),
  invoiceTotal,
  invoiceSupplierId,
}: {
  documents: RelatedDocumentData[];
  matches: Map<string, number[]>;
  /** Rows a tentative split suggestion would match; empty when there is none. */
  proposed?: Map<string, number[]>;
  invoiceTotal: MoneyInput | null | undefined;
  invoiceSupplierId?: string | null;
}): RelatedLinesSummary {
  const groups = documents.map((document): RelatedDocumentGroup => {
    const rows = documentRows(document, matches, proposed, invoiceSupplierId);
    return {
      entityType: document.entityType,
      entityId: document.entityId,
      rows,
      subtotal: sumRows(rows.filter((row) => row.inactiveReason === null)),
    };
  });

  const activeRows: RelatedLineRow[] = [];
  const matchedRows: RelatedLineRow[] = [];
  const proposedRows: RelatedLineRow[] = [];
  for (const group of groups) {
    for (const row of group.rows) {
      if (row.inactiveReason !== null) continue;
      activeRows.push(row);
      if (row.matchedInvoiceLineNumbers.length > 0) matchedRows.push(row);
      else if (row.proposedInvoiceLineNumbers.length > 0) {
        proposedRows.push(row);
      }
    }
  }
  const relatedTotal = sumMoney(groups.map((group) => group.subtotal));
  const hasInvoiceTotal =
    invoiceTotal !== null && invoiceTotal !== undefined && invoiceTotal !== '';

  return {
    groups,
    relatedTotal: serializeMoney(relatedTotal),
    matchedTotal: sumRows(matchedRows),
    proposedTotal: sumRows(proposedRows),
    proposedCount: proposedRows.length,
    unmatchedCount:
      activeRows.length - matchedRows.length - proposedRows.length,
    invoiceTotal: hasInvoiceTotal ? serializeMoney(invoiceTotal) : null,
    difference: hasInvoiceTotal
      ? serializeMoney(subtractMoney(invoiceTotal, relatedTotal))
      : null,
  };
}

export function hasDifference(summary: RelatedLinesSummary): boolean {
  return summary.difference !== null && !isZeroMoney(summary.difference);
}
